import { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Box, Divider } from "@mui/material";
import { ContentUpdates } from "protogen/notes_pb";
import {
  GetDocumentRequest,
  UpdateDocumentRequest,
  CopyDocumentRequest,
} from "protogen/documents_service_pb";
import {
  Document,
  DocumentType,
  DocumentScope,
  Permission,
} from "protogen/documents_pb";
import {
  useGetDocument,
  useUpdateDocument,
  useCopyDocument,
} from "services/documents";
import { FormRenderer, FormStructure } from "components/forms/FormRenderer";
import FlexPage from "components/layout/FlexPage";
import useIsMobile from "components/hooks/useIsMobile";
import FormCreator from "components/forms/FormCreator";
import AdditionalOptions from "components/forms/AdditionalOptions";
import { createDefaultForm } from "components/forms/utils";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import { DocumentFavoriteIcon } from "components/documents/DocumentFavoriteIcon";
import CheckedDropDown from "components/common/CheckedDropDown";
import { getScopeLabel } from "components/documents/utils";
import DropdownMenu from "components/common/DropdownMenu";
import FlexBox from "components/common/FlexBox";
import { Ellipsis } from "lucide-react";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import { DocumentStatus } from "protogen/documents_pb";
import Breadcrumbs from "components/common/Breadcrumbs";
import CopyToButton from "components/documents/CopyToButton";
import { createMenuOption } from "components/common/PopperMenu";
import { PlainMessage } from "@bufbuild/protobuf";
import { UploadAttachment, Attachment } from "protogen/common_pb";
import Loading from "components/common/Loading";
import FormResults from "components/forms/FormResults";

export default ({ mode }: { mode: "create" | "complete" | "view" }) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const breadcrumbs = [
    { name: "Documents", link: "/documents" },
    { name: "Forms", back: true },
  ];

  const setFormStates = (document: Document) => {
    setDocument(document);
    try {
      const meta = document?.meta ? JSON.parse(document?.meta) : {};
      setFormInstanceScope(meta?.formInstanceScope);
      setFormNotifications(meta?.formNotifications || []);
      setMultiInstance(meta?.multiInstance || false);
    } catch (e) {
      setFormInstanceScope(undefined);
      setFormNotifications([]);
      setMultiInstance(false);
    }
  };
  const {
    request: getDocumentRequest,
    error: getDocumentError,
    loading: getDocumentLoading,
  } = useGetDocument((r) => {
    setFormStates(r.document);
    try {
      if (r.document?.note?.content?.payload !== "{}") {
        const loadedForm = JSON.parse(r.document.note.content.payload);
        const attachments = r.document?.note?.content?.attachments || [];
        const formWithAttachments = distributeAttachmentsToFields(
          loadedForm,
          attachments,
        );
        // Ensure existing forms have empty options for editing (migration)
        setForm(
          mode === "create"
            ? ensureEmptyOptions(formWithAttachments)
            : formWithAttachments,
        );
      } else if (mode === "create") {
        setForm(createDefaultForm());
      }
      // If someone somehow navigtes to form creation with a form definition
      // Set the mode to complete
      const meta = r.document?.meta ? JSON.parse(r.document.meta) : {};
      if (!meta?.isFormDefinition && mode === "create") {
        navigate(`/forms/complete/${r.document.uuid}`);
      }
      // Update URL with new UUID if it was copied from a form definition
      if (r.document?.uuid && r.document.uuid !== params.documentRef) {
        navigate(`/forms/${mode}/${r.document.uuid}`, { replace: true });
      }
    } catch (e) {
      setError("Error loading form");
    }
  });
  const { request: updateDocumentRequest, loading } = useUpdateDocument((r) => {
    setFormStates(r.document);
    try {
      const loadedForm = JSON.parse(r.document?.note?.content?.payload);
      const attachments = r.document?.note?.content?.attachments || [];
      const formWithAttachments = distributeAttachmentsToFields(
        loadedForm,
        attachments,
      );
      // Ensure existing forms have empty options for editing (migration)
      setForm(
        mode === "create"
          ? ensureEmptyOptions(formWithAttachments)
          : formWithAttachments,
      );
    } catch (e) {
      setError("Error loading form");
    }
  });

  const { request: copyDocumentRequest } = useCopyDocument((r) => {
    setFormStates(r.document);
    try {
      const loadedForm = JSON.parse(r.document?.note?.content?.payload);
      const attachments = r.document?.note?.content?.attachments || [];
      const formWithAttachments = distributeAttachmentsToFields(
        loadedForm,
        attachments,
      );
      // Ensure existing forms have empty options for editing (migration)
      setForm(
        mode === "create"
          ? ensureEmptyOptions(formWithAttachments)
          : formWithAttachments,
      );
    } catch (e) {
      setError("Error loading form");
    }
  });
  const [form, setForm] = useState<FormStructure | undefined>(undefined);
  const [document, setDocument] = useState<Document | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [formInstanceScope, setFormInstanceScope] = useState<
    string | undefined
  >(undefined);
  const [formNotifications, setFormNotifications] = useState<string[]>([]);
  const [multiInstance, setMultiInstance] = useState<boolean>(false);
  const [copyPopperOpen, setCopyPopperOpen] = useState(false);
  const copyToOptions = useMemo(
    () => [
      createMenuOption(
        "My documents",
        () => {
          if (document) {
            onCopyDocumentHandler(document);
          }
        },
        "",
      ),
    ],
    [document],
  );

  // Initialize viewMode from query parameters (only for create mode)
  const getInitialViewMode = (): "edit" | "view" | "results" => {
    if (mode !== "create") return "edit";
    const viewModeParam = searchParams.get("viewMode");
    if (viewModeParam === "view") return "view";
    if (viewModeParam === "results") return "results";
    return "edit";
  };

  const [viewMode, setViewMode] = useState<"edit" | "view" | "results">(
    getInitialViewMode,
  );

  const confirmState = useConfirmationDialog();

  const onScopeChange = (scope: DocumentScope) => {
    updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        documentType: DocumentType.FORM,
        scope: scope,
      }),
    );
  };

  const onFormInstanceScopeChange = (scope: string) => {
    updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        documentType: DocumentType.FORM,
        updatedFormInstanceScope: scope,
      }),
    );
  };

  const onFormNotificationsChange = (notifications: string[]) => {
    updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        documentType: DocumentType.FORM,
        updatedFormNotifications: notifications,
        shouldUpdateFormNotifications: true,
      }),
    );
  };

  const onMultiInstanceChange = (multiInstance: boolean) => {
    updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        documentType: DocumentType.FORM,
        updatedFormMultiInstance: multiInstance,
        shouldUpdateFormMultiInstance: true,
      }),
    );
  };

  // Update query parameters when viewMode changes (only for create mode)
  const updateViewMode = (newViewMode: "edit" | "view" | "results") => {
    setViewMode(newViewMode);

    if (mode === "create") {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newViewMode === "view") {
        newSearchParams.set("viewMode", "view");
      } else if (newViewMode === "results") {
        newSearchParams.set("viewMode", "results");
      } else {
        newSearchParams.delete("viewMode");
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const distributeAttachmentsToFields = (
    formStructure: FormStructure,
    attachments: Attachment[],
  ): FormStructure => {
    const attachmentsByField = new Map<string, Attachment[]>();

    attachments.forEach((attachment) => {
      if (attachment.inlineReference) {
        if (!attachmentsByField.has(attachment.inlineReference)) {
          attachmentsByField.set(attachment.inlineReference, []);
        }
        attachmentsByField.get(attachment.inlineReference)!.push(attachment);
      }
    });

    const updatedFields = formStructure.fields.map((field) => {
      if (field.type === "attachment") {
        const fieldAttachments = attachmentsByField.get(field.id) || [];
        return {
          ...field,
          value: fieldAttachments,
        };
      }
      return field;
    });

    return {
      ...formStructure,
      fields: updatedFields,
    };
  };

  // Helper function to ensure radio/checkbox fields have an empty option at the end for editing
  const ensureEmptyOptions = (form: FormStructure): FormStructure => {
    return {
      ...form,
      fields: form.fields.map((field) => {
        if (field.type === "radio" || field.type === "checkbox") {
          // Skip adding empty options for Yes/No fields
          const isYesNoField =
            field.type === "radio" &&
            field.options?.length === 2 &&
            field.options[0]?.label === "Yes" &&
            field.options[1]?.label === "No";

          if (!isYesNoField) {
            const hasEmptyOption = field.options.some(
              (opt) => opt.label.trim() === "",
            );
            if (!hasEmptyOption) {
              return {
                ...field,
                options: [...field.options, { value: "", label: "" }],
              };
            }
          }
        }
        return field;
      }),
    };
  };

  // Helper function to clean form data by removing empty options
  const cleanFormData = (form: FormStructure): FormStructure => {
    return {
      ...form,
      fields: form.fields.map((field) => {
        if (field.type === "radio" || field.type === "checkbox") {
          return {
            ...field,
            options: field.options.filter((opt) => opt.label.trim() !== ""),
          };
        }
        return field;
      }),
    };
  };

  const clearAttachments = (form: FormStructure): FormStructure => {
    return {
      ...form,
      fields: form.fields.map((field) => {
        if (field.type === "attachment") {
          return {
            ...field,
            value: [],
          };
        }
        return field;
      }),
    };
  };

  const clearAllFieldValues = (form: FormStructure): FormStructure => {
    return {
      ...form,
      fields: form.fields.map((field) => {
        switch (field.type) {
          case "text":
          case "radio":
            return { ...field, value: "" };
          case "checkbox":
            return { ...field, value: [] };
          case "location":
            return { ...field, value: undefined };
          default:
            return field;
        }
      }),
    };
  };

  const onSubmit = async () => {
    const cleanedForm = cleanFormData(form!);

    const allAttachments: PlainMessage<UploadAttachment>[] = [];

    cleanedForm.fields.forEach((field) => {
      if (
        field.type === "attachment" &&
        field.value &&
        field.value.length > 0
      ) {
        const uploadAttachments = field.value.map((uploadAttachment: any) => ({
          ...uploadAttachment,
          inlineReference: field.id,
        }));
        allAttachments.push(...uploadAttachments);
      }
    });

    let formToSave = clearAttachments(cleanedForm);
    if (mode === "create") {
      formToSave = clearAllFieldValues(formToSave);
    }

    await updateDocumentRequest(
      new UpdateDocumentRequest({
        title: cleanedForm?.title || "",
        documentRef: document?.ref || "",
        content: new ContentUpdates({
          contentType: "json",
          payload: JSON.stringify(formToSave),
          textContent: "",
          attachments: allAttachments,
        }),
        documentType: DocumentType.FORM,
        shouldUpdateContent: true,
        shouldUpdateTitle: true,
      }),
    );
  };

  useEffect(() => {
    if (!params.documentRef) return;
    getDocumentRequest(
      new GetDocumentRequest({
        documentRef: params.documentRef,
        documentType: DocumentType.FORM,
        isFormDefinition: mode === "create",
      }),
    );
  }, [mode, params.documentRef]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: prev.fields.map((field) =>
          field.id === fieldId ? { ...field, value } : field,
        ),
      };
    });
  };

  const onDeleteDocument = async () => {
    if (!document) return;
    confirmState.openDialog(async () => {
      await updateDocumentRequest(
        new UpdateDocumentRequest({
          documentRef: document.ref,
          status: DocumentStatus.DELETED,
        }),
      );
      navigate(-1);
    });
  };

  const copyDocument = async (
    document: Document,
    toDocumentType: DocumentType,
    toEntityType: string,
    toEntityRef: string,
  ) => {
    const result = await copyDocumentRequest(
      new CopyDocumentRequest({
        fromDocumentType: document?.documentType,
        fromEntityType: "advisor",
        fromEntityRef: document?.ref,
        toDocumentType: toDocumentType,
        toEntityType: toEntityType,
        toEntityRef: toEntityRef,
        creationSourceName: "form",
        title: document?.title,
        replaceTitle: true,
      }),
    );
    return result;
  };

  const onCopyDocumentHandler = async (doc: Document | undefined) => {
    const result = await copyDocument(
      doc!,
      DocumentType.FORM,
      "advisor",
      currentUser.ref,
    );
    setCopyPopperOpen(false);
    navigate(
      `/forms/create/${encodeURIComponent(
        result?.document?.uuid || "",
      )}?created=1`,
    );
  };

  return error || getDocumentError || (!form && !getDocumentLoading) ? (
    <Box sx={{ padding: "32px" }}>
      <Typography>{error || "Error loading form"}</Typography>
    </Box>
  ) : getDocumentLoading || !form ? (
    <Loading />
  ) : (
    <FlexPage
      fullHeight
      headerPadding={false}
      sx={{
        maxWidth: "1000px",
        margin: isMobile ? "" : "auto",
        padding: isMobile ? "12px 20px" : "32px 48px",
        paddingBottom: "0px",
        gap: "20px",
        height: "100%",
        overflowY: "auto",
      }}
    >
      {mode === "create" && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Document Action Bar */}
          <FlexBox
            stackedMobile
            justifyContent="space-between"
            alignItems={isMobile ? "start" : "center"}
            gap="12px"
            sx={{ flex: 0 }}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Star */}
              {document && (
                <DocumentFavoriteIcon
                  document={document}
                  showCount={false}
                  showTooltip={false}
                />
              )}

              {/* Visibility scope */}
              {!!document?.scope &&
                document.permission === Permission.WRITE && (
                  <CheckedDropDown
                    key={document.scope}
                    radio={true}
                    disabled={false}
                    displayValue={getScopeLabel(document.scope)}
                    hasIcon={false}
                    sx={{
                      color: "#6B6E7B",
                      "& .MuiOutlinedInput-root": { paddingRight: "0" },
                    }}
                    sxSelect={{
                      fieldset: {
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        paddingRight: "0px",
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                        {
                          border: "none !important",
                        },
                      ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple":
                        {
                          paddingRight: "0px",
                          paddingLeft: "0px",
                          marginTop: "0px",
                          span: {
                            color: "#6B6E7B",
                            fontWeight: 600,
                          },
                        },
                    }}
                    options={[
                      {
                        label: "Anyone with the link",
                        value: DocumentScope.PUBLIC.toString(),
                        defaultChecked:
                          document?.scope === DocumentScope.PUBLIC,
                        mutuallyExclusive: true,
                      },
                    ].concat(
                      isAdmin
                        ? [
                            {
                              label: "Admin",
                              value: DocumentScope.ADMIN.toString(),
                              defaultChecked:
                                document?.scope === DocumentScope.ADMIN,
                              mutuallyExclusive: true,
                            },
                            {
                              label: "Admin discoverable",
                              value:
                                DocumentScope.ADMIN_DISCOVERABLE.toString(),
                              defaultChecked:
                                document?.scope ===
                                DocumentScope.ADMIN_DISCOVERABLE,
                              mutuallyExclusive: true,
                            },
                          ]
                        : [],
                    )}
                    onChange={(r) => {
                      const value = parseInt(r[0].value);
                      onScopeChange(value as DocumentScope);
                    }}
                  />
                )}

              {!!document?.scope && document.permission === Permission.READ && (
                <Typography
                  variant="body"
                  color="text.tertiary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  View only
                </Typography>
              )}

              <CopyToButton
                isOpen={copyPopperOpen}
                menuOptions={copyToOptions}
                setIsOpen={setCopyPopperOpen}
              />

              <DropdownMenu
                disabled={false}
                fullWidth
                buttonContent={<Ellipsis size={20} stroke="#6B6E7B" />}
                dropdownIcon={false}
                variant="text"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                options={[
                  {
                    title: "Delete",
                    onClick: () => onDeleteDocument(),
                  },
                ]}
                sx={{ minWidth: "auto" }}
              />
            </Box>
          </FlexBox>
          <Divider sx={{ marginBottom: "16px" }} />
          <Box
            sx={{
              minHeight: 0,
              flexGrow: 1,
              overflow: "auto",
              marginRight: isMobile ? "-20px" : "-48px",
              paddingRight: isMobile ? "20px" : "48px",
              paddingBottom: "24px",
            }}
          >
            {viewMode === "edit" ? (
              <FormCreator
                form={form}
                onChange={setForm}
                uuid={document?.uuid || ""}
                onSubmit={onSubmit}
                isSubmitting={loading}
                viewMode={viewMode}
                setViewMode={updateViewMode}
                scope={document?.scope || DocumentScope.PRIVATE}
                additionalOptions={
                  isAdmin && viewMode === "edit" ? (
                    <AdditionalOptions
                      onScopeChange={onScopeChange}
                      formInstanceScope={formInstanceScope}
                      onFormInstanceScopeChange={onFormInstanceScopeChange}
                      formNotifications={formNotifications}
                      onFormNotificationsChange={onFormNotificationsChange}
                      multiInstance={multiInstance}
                      onMultiInstanceChange={onMultiInstanceChange}
                    />
                  ) : undefined
                }
              />
            ) : viewMode === "view" ? (
              <FormRenderer
                form={form}
                onFieldChange={handleFieldChange}
                permission={document?.permission}
                mode={mode}
                viewMode={viewMode}
                setViewMode={updateViewMode}
                uuid={document?.uuid || ""}
                additionalOptions={
                  isAdmin && viewMode === "view" ? (
                    <AdditionalOptions
                      onScopeChange={onScopeChange}
                      formInstanceScope={formInstanceScope}
                      onFormInstanceScopeChange={onFormInstanceScopeChange}
                      formNotifications={formNotifications}
                      onFormNotificationsChange={onFormNotificationsChange}
                      multiInstance={multiInstance}
                      onMultiInstanceChange={onMultiInstanceChange}
                    />
                  ) : undefined
                }
              />
            ) : (
              <FormResults
                form={form}
                uuid={document?.uuid || ""}
                mode={mode}
                viewMode={viewMode}
                setViewMode={updateViewMode}
                document={document!}
                additionalOptions={
                  isAdmin ? (
                    <AdditionalOptions
                      onScopeChange={onScopeChange}
                      formInstanceScope={formInstanceScope}
                      onFormInstanceScopeChange={onFormInstanceScopeChange}
                      formNotifications={formNotifications}
                      onFormNotificationsChange={onFormNotificationsChange}
                      multiInstance={multiInstance}
                      onMultiInstanceChange={onMultiInstanceChange}
                    />
                  ) : undefined
                }
              />
            )}
          </Box>
        </Box>
      )}
      {mode === "complete" && (
        <Box sx={{ paddingBottom: "24px" }}>
          <FormRenderer
            form={form}
            onFieldChange={handleFieldChange}
            permission={document?.permission}
            onSubmit={onSubmit}
            mode={mode}
            viewMode={viewMode}
            setViewMode={updateViewMode}
            uuid={document?.uuid || ""}
            submitter={document?.owner}
            submitterDate={new Date(Number(document?.lastUpdatedSec) * 1000)}
          />
        </Box>
      )}
      <ConfirmationDialog
        content={"Are you sure you want to remove this form?"}
        {...confirmState.dialogProps}
      />
    </FlexPage>
  );
};
